<script>
import BaseInputFloatingLabel from '@components/BaseInputFloatingLabel'
import { merchantComputed } from '@state/helpers'
import formatDate, { formatRequestData, lunarDateTime } from '@utils/format-date'

const minutesOfTime = (time = '00:00') => {
  const timeMatches = time.match(/^(\d+):(\d+)$/i)
  const [, hours, minutes] = timeMatches
  return Number(hours) * 60 + Number(minutes)
}

const initData = () => {
  return {
    state: 1,
    introtext: '',
    description: '',
    basePrice: 0,
    price: 0,
    size: 1,
    saleStartDate: '',
    saleStartTime: '',
    saleEndDate: null,
    saleEndTime: null,
    infinity: 0,
    inventory: 1,
    isSaleTimeLimited: 0,
  }
}

const formatDefaultValue = (initData, defaultValue) => {
  let saleStartDate = null
  let saleStartTime = null
  let saleEndDate = null
  let saleEndTime = null
  if (defaultValue.saleStartDate) {
    let startTime = defaultValue.saleStartDate.split(' ')[1]
    saleStartDate = defaultValue.saleStartDate.split(' ')[0]
    saleStartTime = `${startTime.split(':')[0]}:${startTime.split(':')[1]}`
  }
  if (defaultValue.saleEndDate) {
    let endTime = defaultValue.saleEndDate.split(' ')[1]
    saleEndDate = defaultValue.saleEndDate.split(' ')[0]
    saleEndTime = `${endTime.split(':')[0]}:${endTime.split(':')[1]}`
  }
  return {
    ...Object.assign(initData, defaultValue),
    saleStartDate,
    saleStartTime,
    saleEndDate,
    saleEndTime,
  }
}

export default {
  components: {
    BaseInputFloatingLabel,
  },
  props: {
    isDisabled: {
      type: Boolean,
      default: false,
    },
    defaultValue: {
      type: Object,
      default: () => initData(),
    },
    inventoryId: {
      type: String,
      default: '',
    },
    itemId: {
      type: String,
      default: '',
    },
    lightTitle: {
      type: String,
      default: '光明燈',
    },
  },
  data() {
    const submitData = formatDefaultValue(initData(), this.defaultValue)
    return {
      submitData,
      canSetPrice: false,
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 8.64e7
        },
      },
      tryingStoreSetting: false,
    }
  },
  computed: {
    ...merchantComputed,
    submitRules() {
      return {
        title: [
          {
            required: true,
            message: '請填寫方案名稱',
            trigger: 'blur',
          },
        ],
        description: [
          {
            required: true,
            message: '請填寫方案內容',
            trigger: 'blur',
          },
        ],
        basePrice: [
          {
            required: true,
            message: '請輸入原始價格',
            trigger: 'change',
          },
        ],
        price: [
          {
            required: true,
            message: '請輸入販售價格',
            trigger: 'change',
          },
        ],
        size: [
          {
            required: true,
            message: '請輸入使用人數',
            trigger: 'blur',
          },
        ],
        inventory: [
          {
            required: true,
            message: '請輸庫存數量',
            trigger: 'blur',
          },
        ],
        saleStartDate: [
          {
            required: true,
            message: '請選擇起始日期',
            trigger: 'change',
          },
        ],
        saleStartTime: [
          {
            validator: (rule, value, callback) => {
              if (!value) return callback(new Error('請選擇時段開始時間'))
              if (this.submitData.saleEndTime) this.$refs.form.validateField('saleEndTime')

              callback()
            },
            trigger: 'change',
          },
        ],
        saleEndDate: [
          {
            validator: (rule, value, callback) => {
              if (this.submitData.saleEndTime === null) {
                if (value === null) {
                  this.$refs.saleEndDate.resetField()
                  this.$refs.saleEndTime.resetField()
                }
                callback()
                return
              }
              if (!value && this.submitData.saleEndTime !== null) return callback(new Error('請選擇結束日期'))
              if (this.submitData.saleEndTime) this.$refs.form.validateField('saleEndTime')
              callback()
            },
            trigger: 'change',
          },
        ],
        saleEndTime: [
          {
            validator: (rule, value, callback) => {
              if (this.submitData.saleEndDate === null) {
                if (value === null) {
                  this.$refs.saleEndTime.resetField()
                  this.$refs.saleEndDate.resetField()
                }
                callback()
                return
              }
              if (this.submitData.saleEndDate !== null) if (!value) return callback(new Error('請選擇時段結束時間'))

              const { saleStartDate, saleEndDate, saleStartTime } = this.submitData
              const isSameDate = saleStartDate instanceof Date ? saleStartDate.getDate() === saleEndDate.getDate() : saleStartDate === saleEndDate
              if (isSameDate && minutesOfTime(saleStartTime) >= minutesOfTime(value)) return callback(new Error('結束時間不可早於開始時間'))
              callback()
            },
            trigger: 'change',
          },
        ],
      }
    },
    availableAmount() {
      const amount = this.submitData.inventory - this.defaultValue.qty
      return amount < 0 ? 0 : amount
    },
    productId() {
      return this.$route.params.productId
    },
    isEditMode() {
      return this.submitData.id !== undefined
    },
  },
  created() {
    this.checkCanSetPrice()
  },
  methods: {
    lunarDateTime,
    emitSave() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.tryToStoreSetting()
        }
      })
    },
    async checkCanSetPrice() {
      if (!this.$can('api', 'setPrice')) {
        const merchantInfo = this.currentMerchant
        if (merchantInfo === '' || merchantInfo.cashflowSetting !== 1) {
          const settings = await this.$store.dispatch('merchant/fetchSingle')
          this.canSetPrice = settings.cashflowSetting === 1
          return
        }
      }

      this.canSetPrice = this.$can('api', 'setPrice')
    },

    formatSubmitData() {
      const { saleStartDate, saleEndDate, saleStartTime, saleEndTime, ...restSubmitData } = this.submitData
      return {
        itemId: this.itemId,
        ...restSubmitData,
        saleStartDate: saleStartDate === null ? '' : formatRequestData(saleStartDate) + ' ' + saleStartTime,
        saleEndDate: saleEndDate === null ? '' : formatRequestData(saleEndDate) + ' ' + saleEndTime,
      }
    },
    tryToStoreSetting() {
      this.$emit('save-setting', this.formatSubmitData())
    },
    reset() {
      this.submitData = formatDefaultValue(initData(), this.defaultValue)
    },
    onSaleTimeChanged(value) {
      if(value){
        const today = new Date()
        this.submitData.saleStartDate = formatDate(today, 'dateTime')
        this.submitData.saleStartTime = formatDate(today, 'dateTime').split(' ')[1]
      } else {
        this.submitData.saleStartDate = null
        this.submitData.saleStartTime = null
      }
    }
  },
}
</script>

<template>
  <div :disabled="isDisabled">
    <el-form
      ref="form"
      :model="submitData"
      :rules="submitRules"
      label-position="top"
      class="el-form--floating"
      style="padding: 10px;
    padding-top: 0;"
    >
      <el-row :gutter="10">
        <el-col :span="24"
          ><h3 style="margin-bottom: 40px;">{{ lightTitle }}</h3></el-col
        >
        <el-col :span="24">
          <el-form-item label="是否販售" prop="state">
            <el-switch v-model="submitData.state" active-text="開" inactive-text="關" :active-value="1" :inactive-value="0"></el-switch>
          </el-form-item>
        </el-col>

        <el-col :span="12">
          <el-form-item label="原價" prop="basePrice">
            <el-tooltip :disabled="canSetPrice" effect="dark" content="申請金流並開通後即可編輯價格" placement="top">
              <BaseInputFloatingLabel v-model.number="submitData.basePrice" :disabled="!canSetPrice" min="0" />
            </el-tooltip>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="售價" prop="price">
            <BaseInputFloatingLabel v-model.number="submitData.price" :disabled="!canSetPrice" min="0" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="10">
        <el-col :span="15"
          ><el-form-item label="年度庫存數量" prop="inventory">
            <BaseInputFloatingLabel v-model.number="submitData.inventory" :class="$style['price-qty']" min="1">
              <div v-if="defaultValue.qty !== undefined" slot="append" style="font-size: 12px;">已售出：{{ defaultValue.qty }}</div>
            </BaseInputFloatingLabel>
          </el-form-item></el-col
        >
        <el-col v-if="defaultValue.qty !== undefined" :span="9"
          ><el-form-item label="可販售數量"> <el-input v-model="availableAmount" disabled></el-input> </el-form-item
        ></el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="24">
          <el-form-item label="此年度開賣時間">
            <el-radio-group v-model="submitData.isSaleTimeLimited" @change="onSaleTimeChanged">
              <el-radio :label="0">上架即販售</el-radio>
              <el-radio :label="1">限時販售</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-col>
        <template v-if="submitData.isSaleTimeLimited">
          <el-col :span="24">
            <el-form-item :class="$style['form-item--split']" label="開賣日期與時間" required>
              <el-form-item class="el-form-item--normal-margin" prop="saleStartDate">
                <el-date-picker v-model="submitData.saleStartDate" format="yyyy/MM/dd" type="date" :clearable="false"></el-date-picker>
              </el-form-item>
              <el-form-item class="el-form-item--normal-margin" prop="saleStartTime" style="flex-shrink: 2;" :class="$style['width-sm']">
                <el-time-picker v-model="submitData.saleStartTime" format="HH:mm" value-format="HH:mm" placeholder="選擇開始時間" :clearable="false"></el-time-picker>
              </el-form-item>
            </el-form-item>
          </el-col>

          <el-col :span="24"><el-form-item :class="$style['lunar-date-time']" :label="`農曆：${lunarDateTime(submitData.saleStartDate, submitData.saleStartTime)}`"></el-form-item></el-col>

          <el-col :span="24">
            <el-form-item :class="$style['form-item--split']" label="結束日期與時間" :required="submitData.saleEndDate !== null ? true : false">
              <el-form-item ref="saleEndDate" class="el-form-item--normal-margin" prop="saleEndDate">
                <el-date-picker v-model="submitData.saleEndDate" format="yyyy/MM/dd" type="date" :picker-options="pickerOptions" :clearable="false" :class="$style['width-sm']"></el-date-picker>
              </el-form-item>
              <el-form-item ref="saleEndTime" class="el-form-item--normal-margin" prop="saleEndTime" style="flex-shrink: 2;">
                <el-time-picker v-model="submitData.saleEndTime" format="HH:mm" value-format="HH:mm" placeholder="選擇結束時間" :clearable="false"></el-time-picker>
              </el-form-item>
            </el-form-item>
          </el-col>
          <el-col :span="24"><el-form-item :class="$style['lunar-date-time']" :label="`農曆：${lunarDateTime(submitData.saleEndDate, submitData.saleEndTime)}`"></el-form-item></el-col>
        </template>
      </el-row>
      <div :class="$style.footer">
        <el-button :loading="tryingStoreSetting" :disabled="tryingStoreSetting" style="width:100%;" type="primary" @click="emitSave">
          <BaseIcon name="check" />
          <span>儲存</span>
        </el-button>
        <el-button style="width:100%;margin: 0;margin-top: 10px;" plain @click="reset">取消</el-button>
      </div>
    </el-form>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 2px solid $color-border-lighter;
  h4 {
    display: inline-block;
  }
  &__title {
    display: inline-flex;
    flex: 1;
    align-items: center;
  }
}
.content {
  margin-top: 30px;
}
.switcher {
  margin-right: 10px;
}

.footer {
  padding-bottom: 15px;
  text-align: right;
}

.form-item {
  &--split {
    :global {
      .el-form-item__content {
        display: flex;
        align-items: center;
      }
      .el-form-item + .el-form-item {
        margin-left: 10px;
      }
    }
  }
  &--separator {
    height: 40px;
    line-height: 40px;
    text-align: center;
  }
}
.price-qty {
  :global {
    .el-input-group__append {
      border: none;
      background-color: #f2f2f2;
      color: #2f8d96;
      padding: 0 10px 0 0;
    }
  }
}
.width-sm {
  :global {
    .el-input--suffix .el-input__inner {
      padding-right: 0;
    }
  }
}
.lunar-date-time {
  :global {
    .el-form-item__label {
      color: #6e4302;
      font-weight: 500;
    }
  }
}
</style>
